import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toggleCartOpen, addToCart, removeFromCart, clearFromCart, clearCart, placeOrder } from '../store/slices/appSlice';
import { getCoverImageUrl } from '../pages/CatalogViewPage';
import { getCurrencySymbol } from '../helper/currencyHelper';
import OrderPlacementModal from './OrderPlacementModal';
import { useTranslation } from 'react-i18next';

const CartDrawer = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isCartOpen = useSelector((state) => state.app.gui.isCartOpen);
    const cart = useSelector((state) => state.app.cart);
    const products = useSelector((state) => state.app.products.entities);
    const catalog = useSelector((state) => state.app.catalog.value);
    const owner = useSelector((state) => state.app.user.value);

    const [showOrderModal, setShowOrderModal] = useState(false);
    const [orderSuccess, setOrderSuccess] = useState(false);

    useEffect(() => {
        if (isCartOpen) {
            document.body.style.overflow = 'hidden';
            // Also add a class that could be targeted by CSS for deeper fixes if needed
            document.body.classList.add('cart-open');
        } else {
            document.body.style.overflow = '';
            document.body.classList.remove('cart-open');
        }
        return () => {
            document.body.style.overflow = '';
            document.body.classList.remove('cart-open');
        };
    }, [isCartOpen]);

    const cartProductIds = Object.keys(cart);
    
    const calculatePrice = (price, discount) => {
        if (!price) return 0;
        if (!discount || discount <= 0) return price;
        return price - (price * (discount / 100));
    };

    let totalAmount = 0;
    cartProductIds.forEach(id => {
        const p = products[id];
        if (p) {
            const qty = cart[id];
            totalAmount += calculatePrice(p.price, p.discount) * qty;
        }
    });

    const generateWhatsAppMessage = () => {
        const vendorName = catalog?.name || owner?.nombre || '';
        let msg = t('cartDrawer.whatsapp.greeting', { name: vendorName });
        cartProductIds.forEach(id => {
            const p = products[id];
            if (p) {
                const qty = cart[id];
                const unitPrice = calculatePrice(p.price, p.discount).toFixed(2);
                const currSymbol = getCurrencySymbol(catalog);
                const eachText = t('cartDrawer.whatsapp.each');
                msg += `• *${p.name}* x ${qty} (${currSymbol}${unitPrice} ${eachText})\n`;
            }
        });
        const currSymbol = getCurrencySymbol(catalog);
        const totalText = t('cartDrawer.whatsapp.total');
        const closingText = t('cartDrawer.whatsapp.closing');
        msg += `\n${totalText} ${currSymbol}${totalAmount.toFixed(2)}\n\n${closingText}`;
        return encodeURIComponent(msg);
    };

    const phone = owner ? `${owner.cpp || ''}${owner.telefono || ''}` : '';
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${phone}&text=${generateWhatsAppMessage()}`;

    const isDev = process.env.NODE_ENV !== 'production';

    const handleConfirmOrder = async (clientData) => {
        const orderItems = cartProductIds.map(id => {
            const p = products[id];
            const qty = cart[id];
            const unitPrice = calculatePrice(p.price, p.discount);
            return {
                productID: id,
                productName: p.name || 'Producto',
                quantity: qty,
                unitPrice,
                originalPrice: p.price || 0,
                discount: p.discount || 0,
                subtotal: unitPrice * qty
            };
        });

        await dispatch(placeOrder({
            sellerUID: owner?.id,
            catalogID: catalog?.id,
            catalogName: catalog?.name,
            clientName: clientData.clientName,
            clientPhone: clientData.clientPhone,
            clientEmail: clientData.clientEmail,
            items: orderItems,
            currency: getCurrencySymbol(catalog),
            total: totalAmount,
            sendWhatsapp: clientData.sendWhatsapp
        }));

        setShowOrderModal(false);
        setOrderSuccess(true);
        dispatch(clearCart());

        if (clientData.sendWhatsapp) {
            window.open(whatsappUrl, '_blank');
        }
    };

    if (!isCartOpen) return null;

    return (
        <>
            {/* Backdrop to close cart and block background interaction */}
            <div 
                className="cart-backdrop fade show" 
                style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9998, touchAction: 'none' }}
                onClick={() => dispatch(toggleCartOpen(false))}
            />

            <div 
                className="cart-sidebar show" 
                style={{ position: 'fixed', top: 0, right: 0, width: '400px', maxWidth: '100vw', height: '100vh', backgroundColor: '#fff', zIndex: 9999, boxShadow: '-5px 0 25px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', overscrollBehavior: 'contain' }}
            >
                {/* Header */}
                <div className="p-16 border-bottom flex-between align-items-center bg-main-600 text-white">
                    <h5 className="text-white mb-0 flex-align gap-8 text-md fw-bold">
                        <i className="ph ph-shopping-cart text-2xl"></i>
                        {t('cartDrawer.title')} ({cartProductIds.reduce((acc, id) => acc + cart[id], 0)})
                    </h5>

                    <div className="d-flex align-items-center gap-12">
                        <button 
                            type="button" 
                            className="btn-close btn-close-white" 
                            onClick={() => dispatch(toggleCartOpen(false))}
                            aria-label="Close"
                        ></button>
                    </div>
                </div>

                {/* Cart Product List */}
                <div className="p-16 flex-grow-1 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
                    {orderSuccess && (
                        <div className="alert alert-success py-12 px-16 rounded-16 text-xs mb-16 flex-between align-items-center">
                            <div className="d-flex align-items-center gap-8">
                                <i className="ph ph-check-circle text-lg"></i>
                                <span>{t('cartDrawer.orderSuccess')}</span>
                            </div>
                            <button type="button" className="btn-close" onClick={() => setOrderSuccess(false)}></button>
                        </div>
                    )}

                    {cartProductIds.length === 0 ? (
                        <div className="text-center py-60">
                            <i className="ph ph-shopping-cart text-6xl text-gray-300 mb-16 d-block"></i>
                            <p className="text-gray-500 mb-0 fw-medium">{t('cartDrawer.emptyCartTitle')}</p>
                        </div>
                    ) : (
                        cartProductIds.map(id => {
                            const p = products[id];
                            if (!p) return null;
                            const qty = cart[id];
                            const price = calculatePrice(p.price, p.discount);
                            const itemTotal = price * qty;

                            return (
                                <div key={id} className="d-flex align-items-center justify-content-between p-12 rounded-12 bg-gray-50 border mb-8 gap-10 hover-border-gray-300 transition-2">
                                    <img 
                                        src={getCoverImageUrl(p)} 
                                        alt={p.name} 
                                        className="w-48 h-48 rounded-8 object-fit-cover border shadow-2xs flex-shrink-0 bg-white" 
                                    />

                                    <div className="flex-grow-1 overflow-hidden">
                                        <h6 className="text-xs fw-bold mb-2 text-gray-900 text-truncate" title={p.name}>{p.name}</h6>
                                        <span className="text-main-600 fw-bold text-xs d-block">
                                            {getCurrencySymbol(catalog)}{itemTotal.toFixed(2)}
                                        </span>
                                    </div>

                                    <div className="d-flex flex-column align-items-center gap-2 flex-shrink-0">
                                        <div className="d-flex align-items-center border rounded-pill px-6 py-2 bg-white shadow-2xs">
                                            <button 
                                                type="button"
                                                className="btn btn-sm p-0 text-gray-700 border-0 flex-center w-20 h-20 hover-text-main-600 fw-bold" 
                                                onClick={() => dispatch(removeFromCart(id))}
                                            >-</button>
                                            <span className="px-6 text-2xs fw-bold text-gray-900">{qty}</span>
                                            <button 
                                                type="button"
                                                className="btn btn-sm p-0 text-gray-700 border-0 flex-center w-20 h-20 hover-text-main-600 fw-bold" 
                                                onClick={() => dispatch(addToCart(id))}
                                            >+</button>
                                        </div>

                                        {qty > 1 && (
                                            <span className="text-gray-500 fw-medium" style={{ fontSize: '10px' }}>
                                                {getCurrencySymbol(catalog)}{price.toFixed(2)} {t('cartDrawer.whatsapp.each')}
                                            </span>
                                        )}
                                    </div>

                                    <button 
                                        type="button" 
                                        className="btn p-0 bg-danger-50 text-danger-600 border border-danger-200 w-36 h-36 rounded-8 flex-center flex-shrink-0 transition-2 shadow-2xs hover-bg-danger hover-text-white" 
                                        onClick={() => dispatch(clearFromCart(id))}
                                        title={t('cartDrawer.remove')}
                                    >
                                        <i className="ph ph-trash text-lg"></i>
                                    </button>
                                </div>
                            );
                        })
                    )}
                    
                    {cartProductIds.length > 0 && (
                        <div className="mt-24 pt-16 border-top">
                            <button 
                                type="button"
                                className="btn bg-danger-50 text-danger-600 hover-bg-danger hover-text-white border border-danger-200 w-100 py-10 flex-center gap-8 rounded-pill text-sm fw-bold transition-2"
                                onClick={() => dispatch(clearCart())}
                            >
                                <i className="ph ph-trash text-lg"></i>
                                {t('cartDrawer.clearCart')}
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer Summary & Actions */}
                {cartProductIds.length > 0 && (
                    <div className="p-16 border-top bg-gray-50 d-flex flex-column gap-10">
                        <div className="flex-between mb-4">
                            <span className="text-gray-600 fw-bold text-sm">{t('cartDrawer.subtotal')}:</span>
                            <span className="text-xl fw-bold text-main-600">{getCurrencySymbol(catalog)}{totalAmount.toFixed(2)}</span>
                        </div>

                        {/* Direct Order Placement Button (DEV ONLY) */}
                        {isDev && (
                            <button 
                                type="button"
                                onClick={() => setShowOrderModal(true)}
                                className="btn btn-main w-100 py-12 flex-center gap-8 text-white rounded-pill text-sm fw-bold shadow-xs transition-2"
                            >
                                <i className="ph ph-paper-plane-right text-xl"></i>
                                {t('cartDrawer.directOrder')} <span className="badge bg-warning-100 text-warning-700 ms-4 text-3xs">DEV</span>
                            </button>
                        )}

                        <a 
                            href={whatsappUrl}
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="btn btn-success w-100 py-12 flex-center gap-8 text-white rounded-pill text-sm fw-bold shadow-xs transition-2"
                        >
                            <i className="ph ph-whatsapp-logo text-xl"></i>
                            {t('cartDrawer.sendOrderWhatsApp')}
                        </a>

                    </div>
                )}
            </div>

            {/* Order Placement Modal */}
            {isDev && (
                <OrderPlacementModal 
                    show={showOrderModal}
                    onClose={() => setShowOrderModal(false)}
                    onConfirmOrder={handleConfirmOrder}
                    catalog={catalog}
                    totalAmount={totalAmount}
                    cartItemsCount={cartProductIds.reduce((acc, id) => acc + cart[id], 0)}
                />
            )}
        </>
    );
};

export default CartDrawer;
